import React from 'react';
import { IconButton } from '@/components/ui/icon-button';
import { FiMaximize2 } from 'react-icons/fi';
import { Tooltip } from '@/components/ui/tooltip.tsx';
import { MIN_TOUCH_TARGET } from '@/constants/layout';

interface ViewerExpandButtonProps {
	onExpand: () => void;
	loading: boolean;
	top?: number | string;
	right?: number | string;
}

/**
 * Expand button for file viewers
 * Provides a consistent expand-to-fullscreen affordance across compact and full viewer modes
 */
const ViewerExpandButton: React.FC<ViewerExpandButtonProps> = ({ onExpand, loading, top = 2, right = 2 }) => {
	const handleExpandClick = (event: React.MouseEvent) => {
		event.stopPropagation();
		onExpand();
	};

	return (
		<Tooltip content="Expand to full screen" openDelay={300} closeDelay={100}>
			{/* IconButton, not Button: Button's size `sm` carries px 3.5, which made this a 44x36
			    stadium rather than the intended circle. IconButton zeroes the padding, so the
			    square 36px minW/h from the size token is what the full border radius rounds. */}
			<IconButton
				aria-label="Expand to full screen"
				size="sm"
				// Both axes, not just minH: a height-only floor would put back the oval this
				// component just stopped being. `9` is size `sm`'s own h/minW, so desktop is unchanged.
				boxSize={{ base: MIN_TOUCH_TARGET, md: '9' }}
				onClick={handleExpandClick}
				disabled={loading}
				variant="plain"
				position="absolute"
				top={top}
				right={right}
				zIndex={1}
				opacity={0}
				_groupHover={{ opacity: 1 }}
				// The white circle sits on a document page, so it needs the ring to read as a control
				// rather than as part of the page. Hover moves ring and icon to brand — the previous
				// resting-only grey gave no feedback once the button had appeared.
				bg="white"
				borderColor="border.default"
				_hover={{ borderColor: 'brand.fg', color: 'brand.fg' }}
				// Not `default`: the preview underneath is itself clickable and shows a pointer, so
				// anything else here reads as the cursor flickering while the document loads.
				_disabled={{ cursor: 'inherit' }}
				transition="opacity 0.2s ease-in-out, border-color 0.15s ease-in-out, color 0.15s ease-in-out"
			>
				<FiMaximize2 />
			</IconButton>
		</Tooltip>
	);
};

export default ViewerExpandButton;
